/**
 * TanStack Query hooks for the Talent Pool feature.
 *
 * Follows the psychometricKeys / usePsychometricTests pattern exactly
 * (PATTERNS.md lines 107-149). Each query fetches an Auth0 access token via
 * getAccessTokenSilently() and passes it to the service, which forwards it as
 * a Bearer header (matching useTenants / useSuggestedSkills). Without it the
 * backend returns 401 and the global handler force-logs-out the user.
 *
 * Query key factory: talentKeys
 * Hooks: useTalents, useTalent, useJobs, useMatchSuggestions
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  TalentPoolListItem,
  TalentProfileDetail,
  TalentMatchSuggestionsPage,
} from '../types/talent.types';
import type { TalentPoolJob } from '../types/talent.types';
import {
  listTalents,
  getTalent,
  listJobs,
  listMatchSuggestions,
  getAssignedTalentIdsForJob,
} from '../services/talentPool.service';
import type {
  ListTalentsOptions,
  MatchSuggestionsOptions,
} from '../services/talentPool.service';

// ── Query keys ─────────────────────────────────────────────────────────────────

export const talentKeys = {
  all: ['talentPool'] as const,
  lists: () => [...talentKeys.all, 'list'] as const,
  list: (opts: ListTalentsOptions) => [...talentKeys.all, 'list', opts] as const,
  detail: (talentId: string) => [...talentKeys.all, 'detail', talentId] as const,
  jobs: () => [...talentKeys.all, 'jobs'] as const,
  matchSuggestions: (jobId: string, opts: { search?: string; excludeAssigned?: boolean }) =>
    [...talentKeys.all, 'matchSuggestions', jobId, opts] as const,
  assignedTalentsForJob: (jobId: string) =>
    [...talentKeys.all, 'assignedTalents', jobId] as const,
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * Fetch talents in the pool. Filtering (search, min YoE, skills) is applied
 * server-side via the recruiter list endpoint.
 */
export const useTalents = (
  opts: ListTalentsOptions = {},
  queryOptions?: { enabled?: boolean },
) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<TalentPoolListItem[], Error>({
    queryKey: talentKeys.list(opts),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await listTalents(token, opts);
      if (response.error) throw new Error(response.error);
      return response.data as TalentPoolListItem[];
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled: queryOptions?.enabled ?? true,
  });
};

/**
 * Fetch a single talent's full profile detail.
 * Only fetches when talentId is a non-empty string.
 */
export const useTalent = (talentId: string | null | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<TalentProfileDetail, Error>({
    queryKey: talentKeys.detail(talentId ?? ''),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getTalent(token, talentId!);
      if (response.error) throw new Error(response.error);
      return response.data as TalentProfileDetail;
    },
    enabled: !!talentId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};

/**
 * Fetch all jobs available for the assign-to-job flow.
 */
export const useJobs = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<TalentPoolJob[], Error>({
    queryKey: talentKeys.jobs(),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await listJobs(token);
      if (response.error) throw new Error(response.error);
      return response.data as TalentPoolJob[];
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};

/**
 * Infinite list of ranked match suggestions for a job.
 *
 * The backend ranks the whole pool by score before paginating, so pages arrive
 * pre-sorted highest→lowest and can simply be concatenated. ``fetchNextPage``
 * loads the next page while any remain (driven by the sheet's scroll sentinel).
 * Only fetches when jobId is a non-empty string.
 */
export const useMatchSuggestions = (
  jobId: string | null | undefined,
  opts: MatchSuggestionsOptions = {},
  queryOptions?: { enabled?: boolean },
) => {
  const { getAccessTokenSilently } = useAuth0();
  const size = opts.size ?? 20;
  return useInfiniteQuery<TalentMatchSuggestionsPage, Error>({
    queryKey: talentKeys.matchSuggestions(jobId ?? '', {
      search: opts.search,
      excludeAssigned: opts.excludeAssigned,
    }),
    queryFn: async ({ pageParam }) => {
      const token = await getAccessTokenSilently();
      const response = await listMatchSuggestions(token, jobId!, {
        ...opts,
        page: pageParam as number,
        size,
      });
      if (response.error) throw new Error(response.error);
      return response.data as TalentMatchSuggestionsPage;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    enabled: (queryOptions?.enabled ?? true) && !!jobId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};

/**
 * Fetch the set of talent IDs already assigned to a given job.
 * Used by AddFromPoolModal to disable already-assigned talent rows.
 * Only fetches when jobId is a non-empty string.
 */
export const useAssignedTalentIds = (jobId: string | null | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<Set<string>, Error>({
    queryKey: talentKeys.assignedTalentsForJob(jobId ?? ''),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getAssignedTalentIdsForJob(token, jobId!);
      if (response.error) throw new Error(response.error);
      return response.data as Set<string>;
    },
    enabled: !!jobId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
